import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Output,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FindingParameters, StepTask } from '@telespot/domain';
import { Category, Option, Step } from '@telespot/protocols/data-access';

type FindingWithMetadata = FindingParameters & {
  metadata: string | string[] | null;
};

@Component({
    selector: 'ts-analysis-input',
    templateUrl: './analysis-input.component.html',
    styleUrls: ['./analysis-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AnalysisInputComponent implements OnChanges {
  @Input() public readonly category: Category;
  /** Same shape as protocol store / ts-protocol-panel. */
  @Input() public readonly options: Record<string, Option> = {};
  @Input() public readonly steps: Step[];
  @Input() public readonly finding: FindingWithMetadata | null = null;
  @Input() public readonly collapsedCategories: Set<string>;
  @Input() public readonly pipelineId: string;
  @Input() public readonly isViewMode: boolean = false;
  /** When true, render only the input body (no mat-expansion); for use inside ts-protocol-panel rows. */
  @Input() public readonly embedded = false;

  @Output() public readonly createFinding = new EventEmitter<any>();
  @Output() public readonly updateFinding = new EventEmitter<any>();

  public StepTask = StepTask;

  public textValue = '';
  public selectedOptions: string[] = [];
  public selectedOption: string | null = null;

  private textDebounceTimer: any;
  private taskFinding: FindingWithMetadata | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.finding) return;

    this.taskFinding = changes.finding.currentValue;
    this.updateInput();
  }

  onTextInputChange(value: string) {
    clearTimeout(this.textDebounceTimer);

    this.textDebounceTimer = setTimeout(() => {
      this.emitEvent(value);
    }, 500);
  }

  onCheckboxChange(event: MatCheckboxChange, optionUuid: string) {
    const updatedSelection = event.checked
      ? [...this.selectedOptions, optionUuid]
      : this.selectedOptions.filter((uuid) => uuid !== optionUuid);

    this.selectedOptions = updatedSelection;
    this.emitEvent(updatedSelection);
  }

  onRadioChange(value: string) {
    this.selectedOption = value;
    this.emitEvent([value]);
  }

  private updateInput() {
    if (!this.category || !this.steps) {
      return;
    }

    switch (this.category.stepTask) {
      case StepTask.TEXT:
        if (!this.taskFinding) {
          this.textValue = '';
          break;
        }

        this.textValue = (typeof this.taskFinding.metadata === 'string' ? this.taskFinding.metadata : '') || '';
        break;

      case StepTask.CLASSIFICATION:
        this.updateClassificationInput();
        break;

      default:
        console.warn(`Unknown task type: ${this.category.stepTask}`);
    }
  }

  private updateClassificationInput(): void {
    const isMultiple = this.steps[this.category.stepId]?.multiple;

    if (!this.taskFinding) {
      if (isMultiple) {
        this.selectedOptions = [];
      } else {
        this.selectedOption = null;
      }
      return;
    }

    const metadataArray = Array.isArray(this.taskFinding.metadata) ? this.taskFinding.metadata : [];

    if (isMultiple) {
      this.selectedOptions = metadataArray;
    } else {
      this.selectedOption = metadataArray.length > 0 ? metadataArray[0] : null;
    }
  }

  private emitEvent(value: string | string[]) {
    if (!this.taskFinding) {
      return this.createFinding.emit({
        pipelineId: this.pipelineId,
        taskId: this.category.stepId,
        taskType: this.category.stepTask,
        value,
      });
    }

    this.updateFinding.emit({
      id: this.taskFinding?.uuid,
      taskType: this.category.stepTask,
      value,
    });
  }
}
